import { useAccessToken, useCurrentProfile, useHasRole } from 'auth/hooks'
import { ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { useSharedWorker } from 'App/hooks/useSharedWorker'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Auth } from '../../amplify'
import { SYSTEM_NOTIFICATIONS } from '../interfaces'
import useGetNotifications from 'notification/hooks/useGetNotifications'
import useBulkOnNotifications from 'notification/hooks/useBulkOnNotifications'
import useDeleteNotification from 'notification/hooks/useDeleteNotification'
import { useNotifications as useNotificationState, useNotificationsInitialized } from 'notification/redux/hooks'
import { useDispatch } from 'react-redux'
import { notificationsInit, notificationUpdate } from 'notification/redux/actions'
import useSetNotificationSettings from 'notification/hooks/useSetNotificationSettings'
import type { Notification } from 'notification/interfaces'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import NotificationPermissionRequestDialog from 'notification/components/NotificationPermissionRequestDialog'
import { useHistory } from 'react-router-dom'
import useGetImportUrl from 'import/hooks/useGetImportUrl'

export interface NotificationHookData {
  notifications: Notification[]
  unread: boolean
  markAllRead: () => Promise<void>
  deleteAll: () => Promise<void>
  deleteOne: (id: number) => Promise<void>
}
interface HandlerPayload {
  data: { action: string; data: any }
}
export const useNotifications = (): NotificationHookData => {
  const getUrl = useGetImportUrl()
  const profile = useCurrentProfile()
  const { notificationSettings } = profile
  const notificationsInitialized = useNotificationsInitialized()
  const notifications = useNotificationState()
  const dispatch = useDispatch()
  const [setNotificationSettings] = useSetNotificationSettings()
  const [unread, setUnread] = useState<boolean>(false)
  const [fetchNotifications] = useGetNotifications()
  const [sendBulk] = useBulkOnNotifications()
  const [deleteNotification] = useDeleteNotification()
  const showPermissionRequestDialog = useShowPrompt(NotificationPermissionRequestDialog)
  const token = useAccessToken()
  const worker = useSharedWorker()
  const isAdmin = useHasRole(ADMIN)
  const canUseAdminNotificationStream = Boolean(notificationSettings?.enabled && isAdmin)
  const notificationPreferenceSettings = notificationSettings?.settings
  const history = useHistory()
  const handler = useCallback(
    async ({ data: { action, data } }: HandlerPayload) => {
      switch (action) {
        case 'event':
          dispatch(notificationUpdate([data, ...notifications]))
          break
        case 'needToken':
          if (!worker || !canUseAdminNotificationStream) break
          try {
            const session = await Auth.currentSession()
            worker.postMessage({
              event: 'connect',
              token: session.getIdToken().getJwtToken(),
              url: process.env.REACT_APP_API_ENDPOINT,
              notificationsEnabled: notificationPreferenceSettings?.[SYSTEM_NOTIFICATIONS],
            })
          } catch (e) {
            // Amplify will redirect/sign out via auth saga if the session is gone.
          }
          break
        case 'navigate':
          window.focus()
          if (/^http/.test(data.url)) {
            window.location.href = data.url
          } else {
            history.push(data.url)
          }
          break
        case 'import_result':
          window.location.href = await getUrl(data.importId)
          break
        default:
      }
    },
    [notifications], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // We put handler in ref to always have the latest version in event handler
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  const requestNotificationPermissions = useCallback(async () => {
    if (
      'Notification' in window &&
      notificationSettings &&
      notificationSettings.enabled &&
      notificationSettings.settings?.[SYSTEM_NOTIFICATIONS] &&
      Notification.permission === 'default'
    ) {
      const allowed = await showPermissionRequestDialog()
      const permission = allowed ? await Notification.requestPermission() : 'denied'
      if (permission === 'denied') {
        await setNotificationSettings({
          ...notificationSettings,
          settings: {
            ...notificationSettings.settings,
            [SYSTEM_NOTIFICATIONS]: false,
          },
        })
      }
    }
  }, [notificationSettings]) // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = useCallback(async () => {
    const unreadIds = notifications
      .filter(({ readAt }) => {
        return !readAt
      })
      .map(({ id }) => id)
    if (unreadIds.length) {
      try {
        // Error handling is done in the sendBulk hook
        await sendBulk({ read: unreadIds })
        dispatch(notificationUpdate(notifications.map(notification => ({ ...notification, readAt: new Date() }))))
      } catch (e) {}
    }
  }, [dispatch, notifications, sendBulk])

  const deleteAll = useCallback(async () => {
    try {
      // Error handling is done in the sendBulk hook
      await sendBulk({ delete: notifications.map(({ id }) => id) })
      dispatch(notificationUpdate([]))
    } catch (e) {}
  }, [dispatch, notifications, sendBulk])

  const deleteOne = useCallback(
    async (id: number) => {
      try {
        // Error handling is done in the deleteNotification hook
        await deleteNotification(id)
        dispatch(notificationUpdate(notifications.filter(notification => notification.id !== id)))
      } catch (e) {}
    },
    [deleteNotification, dispatch, notifications],
  )

  useEffect(() => {
    if (!notificationsInitialized) {
      fetchNotifications().then(existingNotifications => dispatch(notificationsInit(existingNotifications)))
      requestNotificationPermissions()
    }
  }, [notificationsInitialized]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUnread(notifications.some(({ readAt }) => !readAt))
  }, [notifications])

  useEffect(() => {
    if (token && worker && canUseAdminNotificationStream) {
      // EventSource talks plain HTTP(S) — no more `ws://` rewrite. The SharedWorker
      // appends `/events/stream` and the token query param itself; the missed
      // replay cursor is owned by the browser via `Last-Event-Id` on reconnect.
      worker.postMessage({
        event: 'connect',
        token,
        url: process.env.REACT_APP_API_ENDPOINT,
        notificationsEnabled: notificationPreferenceSettings?.[SYSTEM_NOTIFICATIONS],
      })
      const attachedWorkerHandler = (props: HandlerPayload) => handlerRef.current(props)
      const attachedWindowHandler = () => worker?.postMessage({ event: 'disconnect' })

      window.addEventListener('beforeunload', attachedWindowHandler)
      worker.addEventListener('message', attachedWorkerHandler)
      return () => {
        window.removeEventListener('beforeunload', attachedWindowHandler)
        worker.removeEventListener('message', attachedWorkerHandler)
      }
    } else if (worker) {
      worker.postMessage({ event: 'stop' })
    }
  }, [canUseAdminNotificationStream, token, worker, notificationPreferenceSettings])

  useEffect(() => {
    if (worker && canUseAdminNotificationStream) {
      worker.postMessage({
        event: 'changeNotifications',
        notificationsEnabled: notificationPreferenceSettings?.[SYSTEM_NOTIFICATIONS],
      })
    }
  }, [canUseAdminNotificationStream, notificationPreferenceSettings, worker])

  return { notifications, unread, markAllRead, deleteAll, deleteOne }
}
